// Post-B-117 epics + stories reader — replaces the legacy GitHub-Projects
// `project_fields` / `ticket_epic_counts` path used by /tickets/epics and
// the legacy `public.stories` table used by /tickets/stories.
//
// Single query against `public.tickets` returns every epic / user_story
// / task / bug row in one round trip; shaped client-side into:
//
//   fetchEpicsTree() → [{
//     epicKey, id, title, state, metadata,
//     storiesTotal, storiesDone,
//     tasksTotal,   tasksDone,
//     bugsTotal,    bugsDone,
//     donePct
//   }]  — ordered by EPIC_ORDER
//
//   fetchStoriesTree() → [{
//     storyKey, id, title, state, metadata,
//     epicKey, epicTitle,
//     tasksTotal, tasksDone,
//     bugsTotal,  bugsDone,
//     donePct,
//     tasks: [...], bugs: [...]       // thin — just {id, title, state, metadata}
//   }]  — ordered by parent epic (EPIC_ORDER), then storyKey
//
// All reads are workspace-scoped via RLS + explicit workspace_id filter.

import { SUPABASE_URL, restHeaders } from '../../../shared/functions/supabaseConfig.js'

const WORKSPACE_ID = '69e5911201b0ef47b675463f'

// Matches fetchAgentHierarchy.js + fetchCanvasGraph.js ordering.
// `simona` (AI assistant role) is NOT a product epic per Nika 2026-04-24
// — it's a cross-cutting agent role that owns `/agents/*.md` methodology
// files. HIDDEN_EPICS filter keeps it off the epics + stories pages.
const EPIC_ORDER = [
  'tickets', 'framework', 'canvas', 'server', 'integrations',
  'marketplace', 'chat', 'meet', 'symbols-system', 'documents',
  'pricing', 'commercials'
]
const HIDDEN_EPICS = new Set(['simona'])

const _fetchAllTicketRows = async () => {
  const url = `${SUPABASE_URL}/rest/v1/tickets?select=id,external_id,type,title,state,priority,assignee_email,column_key,parent_ticket_id,metadata,updated_at&workspace_id=eq.${WORKSPACE_ID}&type=in.(epic,user_story,task,bug)&deleted=eq.false&limit=3000`
  const res = await fetch(url, { headers: restHeaders() })
  if (!res.ok) return []
  const rows = await res.json()
  return Array.isArray(rows) ? rows : []
}

const _isDone = (r) => r.state === 'done' || r.state === 'CLOSED' || r.state === 'closed' || !!r.metadata?.struck

export const fetchEpicsTree = async () => {
  const rows = await _fetchAllTicketRows()
  if (!rows.length) return []

  const byId = new Map()
  for (const r of rows) byId.set(r.id, r)

  // Index epics by their string key.
  const epicByKey = new Map()
  const epicRowIdToKey = new Map()
  for (const r of rows) {
    if (r.type !== 'epic') continue
    const key = r.metadata?.epicKey || r.external_id?.replace(/^EPIC#/, '')
    if (!key) continue
    if (HIDDEN_EPICS.has(key)) continue
    epicByKey.set(key, {
      epicKey: key,
      id: r.id,
      title: r.title || key.toUpperCase(),
      state: r.state || 'not_started',
      metadata: r.metadata || {},
      storiesTotal: 0, storiesDone: 0,
      tasksTotal: 0,   tasksDone: 0,
      bugsTotal: 0,    bugsDone: 0
    })
    epicRowIdToKey.set(r.id, key)
  }

  // Map stories → parent epic (via parent_ticket_id).
  const storyRowIdToEpicKey = new Map()
  for (const r of rows) {
    if (r.type !== 'user_story') continue
    const epicKey = r.metadata?.epicKey || (r.parent_ticket_id ? epicRowIdToKey.get(r.parent_ticket_id) : null)
    if (!epicKey) continue
    storyRowIdToEpicKey.set(r.id, epicKey)
    const bucket = epicByKey.get(epicKey)
    if (!bucket) continue
    bucket.storiesTotal += 1
    if (_isDone(r)) bucket.storiesDone += 1
  }

  // Tasks + bugs roll up through their story's epic (or directly if no story).
  for (const r of rows) {
    if (r.type !== 'task' && r.type !== 'bug') continue
    const parentKey = r.parent_ticket_id ? (storyRowIdToEpicKey.get(r.parent_ticket_id) || epicRowIdToKey.get(r.parent_ticket_id)) : null
    if (!parentKey) continue
    const bucket = epicByKey.get(parentKey)
    if (!bucket) continue
    if (r.type === 'task') {
      bucket.tasksTotal += 1
      if (_isDone(r)) bucket.tasksDone += 1
    } else {
      bucket.bugsTotal += 1
      if (_isDone(r)) bucket.bugsDone += 1
    }
  }

  // Synthesize any epic in EPIC_ORDER that wasn't in the DB so the
  // page always renders the canonical 13 — matches fetchCanvasGraph.js.
  for (const key of EPIC_ORDER) {
    if (!epicByKey.has(key)) {
      epicByKey.set(key, {
        epicKey: key,
        id: null,
        title: key.toUpperCase(),
        state: 'not_started',
        metadata: {},
        storiesTotal: 0, storiesDone: 0,
        tasksTotal: 0,   tasksDone: 0,
        bugsTotal: 0,    bugsDone: 0
      })
    }
  }

  // Compute donePct = total ratio of done leaves (tasks + bugs).
  for (const e of epicByKey.values()) {
    const total = e.tasksTotal + e.bugsTotal
    const done = e.tasksDone + e.bugsDone
    e.donePct = total ? Math.round((done / total) * 100) : 0
  }

  // Order: EPIC_ORDER first, then any leftover keys alphabetically.
  const ordered = []
  const seen = new Set()
  for (const key of EPIC_ORDER) {
    if (epicByKey.has(key)) { ordered.push(epicByKey.get(key)); seen.add(key) }
  }
  for (const [key, val] of epicByKey) {
    if (!seen.has(key)) ordered.push(val)
  }
  return ordered
}

export const fetchStoriesTree = async () => {
  const rows = await _fetchAllTicketRows()
  if (!rows.length) return []

  // Lookup epic titles for join display. Hidden epics tracked separately
  // so we can drop any story that resolves to one (avoids an UNCATEGORIZED
  // bucket for orphans under EPIC#simona).
  const epicTitleByKey = new Map()
  const epicRowIdToKey = new Map()
  const hiddenEpicRowIds = new Set()
  for (const r of rows) {
    if (r.type !== 'epic') continue
    const key = r.metadata?.epicKey || r.external_id?.replace(/^EPIC#/, '')
    if (!key) continue
    if (HIDDEN_EPICS.has(key)) { hiddenEpicRowIds.add(r.id); continue }
    epicTitleByKey.set(key, r.title || key.toUpperCase())
    epicRowIdToKey.set(r.id, key)
  }

  // Build stories first so we can attach children by story id.
  const storyById = new Map()
  for (const r of rows) {
    if (r.type !== 'user_story') continue
    const storyKey = r.metadata?.storyKey || r.external_id?.replace(/^STORY#/, '') || `#${r.id}`
    // Skip stories whose parent_ticket_id points to a hidden epic row.
    if (r.parent_ticket_id && hiddenEpicRowIds.has(r.parent_ticket_id)) continue
    const epicKey = r.metadata?.epicKey || (r.parent_ticket_id ? epicRowIdToKey.get(r.parent_ticket_id) : null)
    if (!epicKey) continue  // drop orphans rather than bucket them
    if (HIDDEN_EPICS.has(epicKey)) continue
    storyById.set(r.id, {
      storyKey,
      id: r.id,
      title: r.title || storyKey,
      state: r.state || 'not_started',
      metadata: r.metadata || {},
      epicKey,
      epicTitle: epicTitleByKey.get(epicKey) || epicKey.toUpperCase(),
      tasksTotal: 0, tasksDone: 0,
      bugsTotal: 0,  bugsDone: 0,
      tasks: [],
      bugs: []
    })
  }

  for (const r of rows) {
    if (r.type !== 'task' && r.type !== 'bug') continue
    const story = r.parent_ticket_id ? storyById.get(r.parent_ticket_id) : null
    if (!story) continue
    const bucket = r.type === 'task' ? 'tasks' : 'bugs'
    const totalKey = r.type === 'task' ? 'tasksTotal' : 'bugsTotal'
    const doneKey = r.type === 'task' ? 'tasksDone' : 'bugsDone'
    story[bucket].push({ id: r.id, title: r.title, state: r.state, metadata: r.metadata })
    story[totalKey] += 1
    if (_isDone(r)) story[doneKey] += 1
  }

  for (const s of storyById.values()) {
    const total = s.tasksTotal + s.bugsTotal
    const done = s.tasksDone + s.bugsDone
    s.donePct = total ? Math.round((done / total) * 100) : 0
  }

  const ordered = Array.from(storyById.values()).sort((a, b) => {
    const ai = EPIC_ORDER.indexOf(a.epicKey)
    const bi = EPIC_ORDER.indexOf(b.epicKey)
    const aRank = ai === -1 ? 999 : ai
    const bRank = bi === -1 ? 999 : bi
    if (aRank !== bRank) return aRank - bRank
    return a.storyKey.localeCompare(b.storyKey)
  })
  return ordered
}
